import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import de from './locales/de.json';
import deCH from './locales/de-CH.json';
import en from './locales/en.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import it from './locales/it.json';
import nl from './locales/nl.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import th from './locales/th.json';

// Supported UI languages: derived from the bundled locale files. To add a new UI language,
// add a JSON file under ./locales/ and one entry below — no other code changes required.
const RESOURCES = {
  de: { translation: de },
  'de-CH': { translation: deCH },
  en: { translation: en },
  fr: { translation: fr },
  es: { translation: es },
  it: { translation: it },
  nl: { translation: nl },
  ja: { translation: ja },
  ko: { translation: ko },
  th: { translation: th },
} as const;

export const SUPPORTED_LANGS = Object.keys(RESOURCES) as Array<keyof typeof RESOURCES>;
export type Lang = typeof SUPPORTED_LANGS[number];

// Country flag per language — a glanceable visual cue next to a language label (menu picker,
// celebration screen). Regional-indicator emoji render on iOS and modern Android (our two app
// targets); desktop Windows is the only common platform that shows the letter pair instead.
export const LANG_FLAG: Record<Lang, string> = {
  de: '🇩🇪',
  'de-CH': '🇨🇭',
  en: '🇺🇸',
  fr: '🇫🇷',
  es: '🇪🇸',
  it: '🇮🇹',
  nl: '🇳🇱',
  ja: '🇯🇵',
  ko: '🇰🇷',
  th: '🇹🇭',
};

// Each language's name in ITSELF (not translated) — shown on the language-picker cards so a
// child stuck in the wrong language can still recognise their own.
export const LANG_NATIVE: Record<Lang, string> = {
  de: 'Deutsch',
  'de-CH': 'Deutsch (CH)',
  en: 'English',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  nl: 'Nederlands',
  ja: '日本語',
  ko: '한국어',
  th: 'ไทย',
};

/**
 * Region-qualified BCP-47 tag to hand a speech engine for each UI language.
 *
 * Our own tags are deliberately short ("de", "en") because they key the locale bundles and the
 * lesson packs — but a REGION-LESS tag is a poor instruction to a TTS engine. Android's Google TTS
 * resolves a bare "de" inconsistently and frequently falls back to the system default voice, which
 * is how German text ended up being read aloud in an English voice on an English device. Every
 * voice an engine reports (`SpeechSynthesisVoice.lang`) is region-qualified, so speaking in the
 * same vocabulary removes the guesswork.
 *
 * Used only for `SpeechSynthesisUtterance.lang` (see speakRulePrompt in src/game/audio.ts).
 * Voice *matching* stays base-language tolerant there, which is what lets "de-CH" — a tag almost
 * no engine ships a distinct voice for — pick up a "de-DE" voice.
 */
export const TTS_LANG: Record<Lang, string> = {
  de: 'de-DE',
  'de-CH': 'de-CH',
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  nl: 'nl-NL',
  ja: 'ja-JP',
  ko: 'ko-KR',
  th: 'th-TH',
};

/** The tag to speak `lang` in — falls back to the tag itself for anything unshipped. */
export function ttsLang(lang: string): string {
  return TTS_LANG[lang as Lang] ?? lang;
}

const SUPPORTED_BY_LOWER = new Map(SUPPORTED_LANGS.map((l) => [l.toLowerCase(), l] as const));

/**
 * Fold a reported locale onto a language we actually ship — "en-US" → "en", "de-CH" → "de-CH",
 * "de-AT" → "de", "pt-BR" → unchanged (and so onto the German fallback).
 *
 * This runs on every code the detector produces, and it is load-bearing: browsers and WebViews
 * report REGION TAGS, and a bare "en" is the exception, not the rule. Without the fold, one of two
 * things happens depending on `nonExplicitSupportedLngs`, and BOTH are broken:
 *   - true:  "en-US" counts as supported in its own right, so with `load: 'currentOnly'` the
 *            resolve chain is ["en-US", <fallback de>] — the bundled "en" resources are never
 *            consulted and the whole UI renders in GERMAN.
 *   - false: "en-US" is rejected outright and drops straight to the German fallback, unless the
 *            device happens to also list a bare "en" in navigator.languages (many don't — iOS
 *            commonly reports a single tag).
 * On top of the wrong UI language it poisons everything keyed off i18next.language: getActivePack()
 * (game/packs.ts) looks the saved lesson up under "en-US", finds nothing, and the lesson pick
 * silently reverts to the default. The fold keeps i18next.language a tag we ship, which is what the
 * rest of the app assumes. Guarded by language-detection.test.ts.
 */
export function toSupportedLang(detected: string): string {
  const lower = detected.toLowerCase();
  return SUPPORTED_BY_LOWER.get(lower) ?? SUPPORTED_BY_LOWER.get(lower.split('-')[0]) ?? detected;
}

/**
 * Shared init options, exported so language-detection.test.ts exercises the REAL config instead of
 * a copy that could drift away from it.
 */
export const I18N_OPTIONS = {
  fallbackLng: { 'de-CH': ['de'], default: ['de'] },
  supportedLngs: SUPPORTED_LANGS as readonly string[],
  // false, deliberately: with the fold above every detected code is already an exact tag we ship,
  // so accepting non-explicit region variants would only re-open the trap described there.
  nonExplicitSupportedLngs: false,
  load: 'currentOnly',
  resources: RESOURCES,
  interpolation: { escapeValue: false },
  detection: { convertDetectedLanguage: toSupportedLang },
} as const;

export async function initI18n(): Promise<void> {
  await i18next.use(LanguageDetector).init({ ...I18N_OPTIONS });
}

export function setLanguage(lang: Lang): Promise<unknown> {
  return i18next.changeLanguage(lang);
}
